import { Component, Host, h, Prop, Element, Listen } from '@stencil/core';
import { LANGUAGE_CODE } from '../../../constant/language';
import { computePosition, autoUpdate, flip, shift, offset, arrow, Middleware } from '@floating-ui/dom';
import { isNullOrUndefinedOrEmpty } from '../../../utils/stringHelper';
import { delay } from '../../../utils/eventHelper';
@Component({
  tag: 'cms-popover',
  styleUrl: 'cms-popover.scss',
  shadow: false,
})
export class CmsTooltip {
  /**
   * Where to place the floating element relative to its reference element..
   */
  @Prop() dataPlacement:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end' = 'bottom';
  /**
   * The language code of the component.
   */
  @Prop() dataLanguageCode: string = LANGUAGE_CODE.ENGLISH;
  /**
   * Popover trigger mode. Could be hover or click.
   */
  @Prop() dataTrigger: 'hover' | 'click' = 'click';
  /**
   * displaces the floating element from its core placement along the specified axes.
   */
  @Prop() dataOffset: number = 15;
  /**
   * This describes the padding between the arrow and the edges of the floating element.
   */
  @Prop() dataArrowPadding: number = 25;
  /**
   * automatically call an update function when necessary so that the floating element remains anchored to the reference element in a variety of scenarios without detaching.
   */
  @Prop() dataAutoUpdate: boolean = true;
  @Prop() dataKeyboardAccessible: boolean = true;
  @Element() elRoot: HTMLElement;
  private cleanup;
  private referenceEl: HTMLElement;
  private floatingEl: HTMLElement;
  private arrowEl: HTMLElement;
  private closeElList: Element[];
  private slotNameForReference: string = 'reference-element';
  private slotNameForFloating: string = 'floating-element';
  private slotNameForClose: string = 'close';
  private closestPopoverEl: HTMLElement;
  private closestPopoverFloatingEl: HTMLElement;
  componentDidLoad() {
    delay(this.init, 100);
  }
  disconnectedCallback() {
    this.hidePopover();
    if (this.dataTrigger === 'click') {
      if (this.referenceEl) {
        this.referenceEl.removeEventListener('click', this.handleClickReference);
      }
    } else if (this.dataTrigger === 'hover') {
      this.elRoot.removeEventListener('mouseenter', this.showPopover);
      this.elRoot.removeEventListener('mouseleave', this.hidePopover);
    }
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (!this.dataKeyboardAccessible) return;
    if (event.key === 'Escape' && this.getIsFloatingShow()) {
      this.hidePopover();
      event.preventDefault();
    }
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      event.target === this.referenceEl &&
      !this.getIsFloatingShow()
    ) {
      this.showPopover();
      event.preventDefault();
    }
  }
  init = () => {
    this.referenceEl = this.elRoot.querySelector(`[data-slot='${this.slotNameForReference}']`);
    this.floatingEl = this.elRoot.querySelector(`[data-slot='${this.slotNameForFloating}']`);
    this.closeElList = Array.from(this.elRoot.querySelectorAll(`[data-slot='${this.slotNameForClose}']`));
    this.closestPopoverEl = this.elRoot.parentElement.closest('cms-popover');
    if (this.closestPopoverEl) {
      this.closestPopoverFloatingEl = this.closestPopoverEl.querySelector(`[data-slot='${this.slotNameForFloating}']`);
    }
    const divArrow = document.createElement('div');
    divArrow.setAttribute('data-slot', 'arrow');
    this.floatingEl.appendChild(divArrow);
    this.arrowEl = divArrow;
    if (this.dataTrigger === 'click') {
      if (this.referenceEl && this.floatingEl) {
        this.referenceEl.addEventListener('click', this.handleClickReference);
      }
    } else if (this.dataTrigger === 'hover') {
      this.elRoot.addEventListener('mouseenter', this.showPopover);
      this.elRoot.addEventListener('mouseleave', this.hidePopover);
    }
    if (this.referenceEl) {
      this.referenceEl.setAttribute('aria-haspopup', 'dialog');
      this.referenceEl.removeAttribute('aria-selected');
      if (!this.referenceEl.getAttribute('tabindex')) {
        this.referenceEl.setAttribute('tabindex', '0');
      }
    }
    const id = `popover-desc-${Math.floor(Math.random() * 1000)}`;
    if (this.referenceEl) {
      this.referenceEl.setAttribute('aria-describedby', id);
    }
    if (this.floatingEl) {
      const textContent = `Click or press Enter to ${this.dataTrigger === 'click' ? 'open popup content' : 'view more information'}`;
      this.floatingEl.setAttribute('aria-label', textContent);
    }
  };
  updatePosition = () => {
    let middleware: Middleware[] = [offset(this.dataOffset)];
    if (this.dataAutoUpdate) {
      middleware = middleware.concat(flip());
    }
    middleware = middleware.concat([
      shift({ padding: 5 }),
      arrow({ element: this.arrowEl, padding: this.dataArrowPadding }),
    ]);
    computePosition(this.referenceEl, this.floatingEl, {
      placement: this.dataPlacement,
      middleware,
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.floatingEl.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[placement.split('-')[0]];
      Object.assign(this.arrowEl.style, {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-7px',
      });
      this.arrowEl.setAttribute('data-arrow-direction', staticSide);
      this.floatingEl.setAttribute('data-arrow-direction', staticSide);
    });
  };
  handleClickReference = (ev: MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (this.getIsFloatingShow()) {
      this.hidePopover();
    } else {
      this.showPopover();
    }
  };
  handleClickFloating = (ev: MouseEvent) => {
    ev.stopPropagation();
  };
  handleClickClose = (ev: MouseEvent) => {
    ev.stopPropagation();
    this.hidePopover();
  };
  getIsFloatingShow = () => {
    return (
      this.floatingEl &&
      !isNullOrUndefinedOrEmpty(this.floatingEl.style.display) &&
      this.floatingEl.style.display !== 'none'
    );
  };
  showPopover = () => {
    const activeFloatingEl: HTMLElement = document.querySelector(`.active[data-slot="${this.slotNameForFloating}"]`);
    if (activeFloatingEl && !this.closestPopoverEl) {
      activeFloatingEl.style.display = 'none';
      activeFloatingEl.classList.remove('active');
      activeFloatingEl.closest('cms-popover').classList.remove('active');
    }
    this.floatingEl.classList.add('active');
    this.elRoot.classList.add('active');
    this.floatingEl.style.display = 'block';
    if (this.dataTrigger === 'click') {
      if (this.floatingEl) {
        this.floatingEl.addEventListener('click', this.handleClickFloating);
      }
      if (this.closeElList && this.closeElList.length > 0) {
        for (const item of this.closeElList) {
          item.addEventListener('click', this.handleClickClose);
        }
      }
      if (this.referenceEl && this.floatingEl) {
        document.addEventListener('click', this.hidePopover);
        if (this.closestPopoverFloatingEl) {
          this.closestPopoverFloatingEl.addEventListener('click', this.hidePopover);
        }
      }
      if (this.dataAutoUpdate) {
        this.cleanup = autoUpdate(this.referenceEl, this.floatingEl, this.updatePosition);
      } else {
        this.updatePosition();
      }
    } else if (this.dataTrigger === 'hover') {
      this.updatePosition();
    }

    if (this.floatingEl) {
      this.floatingEl.setAttribute('role', 'dialog');
      this.floatingEl.setAttribute('tabindex', '-1');
      setTimeout(() => this.floatingEl.focus(), 50);
    }
    if (this.floatingEl && this.dataTrigger === 'click') {
      const focusableElements = this.floatingEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        this.floatingEl.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey && document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        });
      }
    }
  };
  hidePopover = () => {
    if (this.getIsFloatingShow()) {
      this.floatingEl.style.display = 'none';
      this.floatingEl.classList.remove('active');
      this.elRoot.classList.remove('active');
    }
    if (this.dataTrigger === 'click') {
      if (this.floatingEl) {
        this.floatingEl.removeEventListener('click', this.handleClickFloating);
      }
      if (this.closeElList && this.closeElList.length > 0) {
        for (const item of this.closeElList) {
          item.removeEventListener('click', this.handleClickClose);
        }
      }
      if (this.referenceEl && this.floatingEl) {
        document.removeEventListener('click', this.hidePopover);
        if (this.closestPopoverFloatingEl) {
          this.closestPopoverFloatingEl.removeEventListener('click', this.hidePopover);
        }
      }
      if (this.cleanup) {
        this.cleanup();
      }
      if (this.referenceEl) {
        setTimeout(() => this.referenceEl.focus(), 50);
      }
    }
  };
  render() {
    return (
      <Host style={{ '--offset': `${this.dataOffset}px` }} role="presentation">
        <slot />
      </Host>
    );
  }
}
